import { Box, Heading, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  Populater,
  useGetProductGroupsForSourcingEventQuery,
  type NegotiationRoundV2Dto,
  type ProductGroupDto,
  type ReceivedBidV2Dto,
} from "../../../../autogen/bff-api";

interface Stage {
  key: string;
  label: string;
  bids: ReceivedBidV2Dto[];
}

export const RoundPriceDevelopmentSection = ({
  eventId,
  rounds,
  bids,
}: {
  eventId: string;
  rounds: NegotiationRoundV2Dto[];
  bids: ReceivedBidV2Dto[];
}) => {
  const { t } = useTranslation();
  const { data: groupsData } = useGetProductGroupsForSourcingEventQuery({ sourcingEventId: eventId });

  const groups = groupsData?.productGroups ?? [];
  const roundsWithBids = rounds
    .filter((round) => round.roundNumber != null)
    .sort((a, b) => (a.roundNumber ?? 0) - (b.roundNumber ?? 0))
    .filter((round) => bids.some((bid) => bid.forNegotiationRound === round.id && bid.products?.length));
  const initialBids = bids.filter((bid) => !bid.forNegotiationRound && bid.products?.length);

  if (groups.length === 0 || roundsWithBids.length === 0 || initialBids.length === 0) return null;

  const stages: Stage[] = [
    { key: "initial", label: t("Original bid"), bids: initialBids },
    ...roundsWithBids.map((round) => ({
      key: round.id,
      label: `${t("Round")} ${round.roundNumber}`,
      bids: bids.filter((bid) => bid.forNegotiationRound === round.id),
    })),
  ];
  const suppliers = [...new Map(initialBids.map((bid) => [bid.owner, bid.ownerName])).entries()];

  return (
    <Box pt="6">
      <Heading size="sm" pb="1">
        {t("Price development")}
      </Heading>
      <Text fontSize="sm" color="smMuted" pb="3">
        {t("Unit prices per product across the negotiation rounds.")}
      </Text>
      {groups.map((group) => (
        <GroupDevelopment key={group.id} group={group} stages={stages} suppliers={suppliers} />
      ))}
    </Box>
  );
};

const GroupDevelopment = ({
  group,
  stages,
  suppliers,
}: {
  group: ProductGroupDto;
  stages: Stage[];
  suppliers: [string, string][];
}) => {
  const { t } = useTranslation();
  const nameField = group.productFields.find((field) => field.populatedBy === Populater.Buyer);

  const priceFor = (stage: Stage, supplierOrgId: string, productId: string): number | null =>
    stage.bids
      .find((bid) => bid.owner === supplierOrgId)
      ?.products?.find((g) => g.productGroupId === group.id)
      ?.productBids.find((p) => p.productId === productId)?.unitPrice.value ?? null;

  return (
    <Box pb="6">
      <Text fontWeight="semibold" pb="2">
        {group.name}
      </Text>
      <TableContainer border="1px solid" borderColor="smBorder" rounded="md">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>{nameField?.name ?? t("Product")}</Th>
              <Th>{t("Supplier")}</Th>
              {stages.map((stage) => (
                <Th key={stage.key} isNumeric>
                  {stage.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {group.products.flatMap((product) => {
              const name =
                product.productFields.find((field) => field.name === nameField?.name)?.value ?? "—";
              return suppliers.map(([orgId, orgName], supplierIndex) => {
                const prices = stages.map((stage) => priceFor(stage, orgId, product.id));
                if (prices.every((price) => price == null)) return null;
                return (
                  <Tr key={`${product.id}-${orgId}`}>
                    <Td>{supplierIndex === 0 ? String(name) : ""}</Td>
                    <Td>{orgName}</Td>
                    {prices.map((price, index) => {
                      const previous = prices.slice(0, index).reverse().find((p) => p != null);
                      const color =
                        price == null || previous == null
                          ? undefined
                          : price < previous
                            ? "green.500"
                            : price > previous
                              ? "red.500"
                              : undefined;
                      return (
                        <Td key={stages[index].key} isNumeric color={color}>
                          {price != null ? price.toLocaleString() : "—"}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              });
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
